"use client";

import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { useReducedMotion } from "motion/react";

interface StackItem {
  src: string;
  alt: string;
}

/** Personal moments only — portraits, talks, and life outside the work. */
const stackItems: StackItem[] = [
  { src: "/images/photo.png", alt: "Portrait of Amilcar" },
  {
    src: "/images/talk-figma-make.jpg",
    alt: "Amilcar presenting Figma Make to a room of designers",
  },
  {
    src: "/images/pedra-bonita.png",
    alt: "Amilcar shooting from Pedra Bonita, Rio de Janeiro",
  },
  {
    src: "/images/talk-classroom.png",
    alt: "Amilcar speaking to a full room of designers",
  },
  {
    src: "/images/cycling.png",
    alt: "Amilcar riding a road bike through the forest",
  },
];

const MAX_VISIBLE = 4;

/**
 * Balanced resting spots the cards cycle through — each deal adds random
 * jitter on top so no two drops ever land the same way.
 */
const POSES = [
  { x: -18, y: 14, rotate: -6 },
  { x: 16, y: -10, rotate: 4.5 },
  { x: -7, y: -16, rotate: -2.5 },
  { x: 12, y: 12, rotate: 7 },
];

/** Card held above the table: broad, soft, distant shadow. */
const LIFTED_SHADOW =
  "0 46px 60px rgba(0,0,0,0.26), 0 12px 28px rgba(0,0,0,0.1)";
/** Card resting on the table: tight ambient + crisp contact shadow. */
const RESTED_SHADOW =
  "0 16px 32px rgba(0,0,0,0.34), 0 3px 8px rgba(0,0,0,0.24)";

/** Fine fractal noise — the paper grain on the frame. */
const PAPER_NOISE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E";

interface DealtCard {
  id: number;
  item: StackItem;
  x: number;
  y: number;
  rotate: number;
  /** how far above the pile the card starts */
  dropHeight: number;
  /** sideways drift while falling */
  drift: number;
  /** entry over-rotation — sign and size vary per card */
  rotateIn: number;
  /** how far the settle dips past the resting spot */
  settleDip: number;
  duration: number;
  /** oldest card, fading out under the pile */
  leaving?: boolean;
}

const rand = (min: number, max: number) => min + Math.random() * (max - min);

const PolaroidStack: React.FC = () => {
  const [deck, setDeck] = useState<DealtCard[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const cardElsRef = useRef(new Map<number, HTMLElement>());
  const enteredRef = useRef(new Set<number>());
  const exitingRef = useRef(new Set<number>());
  const ctxRef = useRef<gsap.Context | null>(null);
  const counterRef = useRef(0);
  const shouldReduceMotion = useReducedMotion();

  // One GSAP context for every tween in the stack; declared before the
  // animation effects below so cleanup and re-entry order out correctly.
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {}, rootRef);
    ctxRef.current = ctx;
    return () => {
      ctx.revert();
      ctxRef.current = null;
      enteredRef.current.clear();
      exitingRef.current.clear();
    };
  }, []);

  useEffect(() => {
    const makeCard = (jitter: boolean): DealtCard => {
      const i = counterRef.current++;
      const pose = POSES[i % POSES.length];
      const spin = Math.random() < 0.5 ? -1 : 1;
      return {
        id: i,
        item: stackItems[i % stackItems.length],
        x: pose.x + (jitter ? rand(-14, 14) : 0),
        y: pose.y + (jitter ? rand(-12, 12) : 0),
        rotate: pose.rotate + (jitter ? rand(-3, 3) : 0),
        dropHeight: rand(55, 90),
        drift: rand(10, 24) * spin,
        rotateIn: rand(5, 10) * spin,
        settleDip: rand(1.5, 3),
        duration: rand(0.85, 1.1),
      };
    };

    if (shouldReduceMotion) {
      setDeck(Array.from({ length: MAX_VISIBLE }, () => makeCard(false)));
      return;
    }

    const root = rootRef.current;
    let inView = false;
    let timeout: number | undefined;

    const deal = () => {
      setDeck((current) => {
        const active = current.filter((card) => !card.leaving);
        const next = [...current, makeCard(true)];
        if (active.length < MAX_VISIBLE) return next;
        // Mark the oldest resting card; its exit tween removes it for real.
        const oldestId = active[0].id;
        return next.map((card) =>
          card.id === oldestId ? { ...card, leaving: true } : card,
        );
      });
    };

    const schedule = (delay: number) => {
      timeout = window.setTimeout(() => {
        // Off-screen or hidden tab: idle instead of piling up unseen deals.
        if (!inView || document.hidden) {
          schedule(900);
          return;
        }
        deal();
        schedule(rand(2800, 4000));
      }, delay);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
      },
      { threshold: 0.25 },
    );
    if (root) observer.observe(root);

    schedule(500);

    return () => {
      if (timeout !== undefined) window.clearTimeout(timeout);
      observer.disconnect();
    };
  }, [shouldReduceMotion]);

  // Entrance: hand lowers the card in, gravity takes it the last stretch,
  // it lands with a dip, the rotation wobbles to rest, the shadow tightens.
  useLayoutEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    deck.forEach((card) => {
      if (card.leaving || enteredRef.current.has(card.id)) return;
      const el = cardElsRef.current.get(card.id);
      if (!el) return;
      enteredRef.current.add(card.id);

      ctx.add(() => {
        if (shouldReduceMotion) {
          gsap.set(el, {
            x: card.x,
            y: card.y,
            rotation: card.rotate,
            opacity: 1,
            boxShadow: RESTED_SHADOW,
          });
          return;
        }

        const fall = card.duration;

        gsap.set(el, {
          x: card.x + card.drift,
          y: card.y - card.dropHeight,
          rotation: card.rotate + card.rotateIn,
          scale: 1.06,
          opacity: 0,
          boxShadow: LIFTED_SHADOW,
        });

        // Barely-there idle drift for the resting card — built up-front so
        // the GSAP context owns it, started only once the landing finishes.
        const float = gsap.timeline({ paused: true, repeat: -1, yoyo: true });
        float.to(el, {
          y: card.y - rand(1.5, 2.5),
          x: card.x + rand(-1, 1),
          rotation: card.rotate + rand(-0.5, 0.5),
          duration: rand(3.5, 5),
          ease: "sine.inOut",
        });

        const tl = gsap.timeline({
          onComplete: () => {
            float.play();
          },
        });
        tl.to(el, { opacity: 1, duration: 0.25, ease: "power1.out" }, 0)
          // the fall — gravity takes it, quick and decisive
          .to(
            el,
            {
              y: card.y + card.settleDip,
              scale: 1,
              duration: fall,
              ease: "power1.in",
            },
            0,
          )
          // one soft air correction on the way down — drifts in, straightens
          .to(
            el,
            {
              x: card.x + card.drift * 0.12,
              rotation: card.rotate - card.rotateIn * 0.18,
              duration: fall,
              ease: "sine.inOut",
            },
            0,
          )
          // touchdown — dip recovers, tiny skid finishes the slide
          .to(el, { y: card.y, duration: 0.3, ease: "power2.out" }, fall)
          .to(el, { x: card.x, duration: 0.35, ease: "power2.out" }, fall)
          // the paper keeps a breath of life as it comes to rest
          .to(
            el,
            {
              rotation: card.rotate,
              duration: 0.7,
              ease: "elastic.out(1, 0.5)",
            },
            fall,
          )
          .to(
            el,
            { boxShadow: RESTED_SHADOW, duration: 0.35, ease: "power2.out" },
            fall * 0.8,
          );
      });
    });
  }, [deck, shouldReduceMotion]);

  // Exit: the oldest card fades out softly underneath the pile.
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;

    deck.forEach((card) => {
      if (!card.leaving || exitingRef.current.has(card.id)) return;
      exitingRef.current.add(card.id);

      const removeCard = () =>
        setDeck((current) => current.filter((c) => c.id !== card.id));

      const el = cardElsRef.current.get(card.id);
      if (!el) {
        removeCard();
        return;
      }

      ctx.add(() => {
        // The idle float owns y; take it over so the card sinks smoothly.
        gsap.killTweensOf(el, "y");
        gsap.to(el, {
          opacity: 0,
          scale: 0.975,
          y: "+=5",
          duration: 1.0,
          ease: "sine.inOut",
          onComplete: () => {
            gsap.killTweensOf(el);
            removeCard();
          },
        });
      });
    });
  }, [deck]);

  return (
    <div
      ref={rootRef}
      role="img"
      aria-label="Polaroid photos of Amilcar — portraits, talks, and moments outside the work"
      className="relative grid w-80 place-items-center sm:w-96 lg:w-[420px] h-[420px] sm:h-[500px] lg:h-[600px]"
    >
      {deck.map((card, index) => (
        <figure
          key={card.id}
          ref={(el) => {
            if (el) cardElsRef.current.set(card.id, el);
            else cardElsRef.current.delete(card.id);
          }}
          className="relative w-[250px] rounded-[4px] p-3 pb-12 opacity-0 will-change-transform [grid-area:1/1] sm:w-[290px] lg:w-[330px] lg:p-4 lg:pb-16"
          style={{
            zIndex: index,
            background:
              "linear-gradient(155deg, #fdfdfb 0%, #f6f4ee 62%, #efece4 100%)",
          }}
        >
          <div className="relative aspect-square w-full overflow-hidden bg-[#e8e6e0]">
            <Image
              src={card.item.src}
              alt={card.item.alt}
              fill
              sizes="(max-width: 639px) 250px, (max-width: 1023px) 290px, 330px"
              draggable={false}
              className="object-cover grayscale contrast-105"
            />
          </div>
          {/* Paper grain + soft sheen over the whole frame */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[4px] opacity-[0.16] mix-blend-multiply"
            style={{ backgroundImage: `url("${PAPER_NOISE}")` }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-[4px]"
            style={{
              background:
                "linear-gradient(120deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0.04) 100%)",
            }}
          />
        </figure>
      ))}
    </div>
  );
};

export default PolaroidStack;
